import React, { useMemo } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query/react'
import Style from './viewSummary.module.css'
import DateRangeFilter from './Components/DateRangeFilter'
import DirectionSwitch from './Components/DirectionSwitch'
import SyncStatus from './Components/SyncStatus'
import KpiStrip from './Components/KpiStrip'
import EntitySummaryTable from './Components/EntitySummaryTable'
import { rangeFromParams, rangeToParams } from './summaryFormat'
import PageLoader from '../../SharedComponents/PageState/PageLoader'
import PageNotice from '../../SharedComponents/PageState/PageNotice'
import { describeConnection, useConnection } from '../../../features/connections/connectionsApi'
import { toEntityRow, useGetSyncTimeQuery } from '../../../features/syncReports/syncReportsApi'
import { DIRECTION, directionFor } from '../../../features/syncReports/recordStatus'
import useEntitySummaries from '../../../features/syncReports/useEntitySummaries'
import { PATH, paths } from '../../../app/routes'
const COUNT_KEYS = ['requestReceived', 'inProcess', 'responseReceived', 'responseTransferred', 'error', 'completed', 'inFlight', 'total']
const sumEntities = (rows) => COUNT_KEYS.reduce((acc, key) => {
    acc[key] = rows.reduce((sum, row) => sum + row[key], 0)
    return acc
}, {})

const syncTimeArgs = (connector, direction) => connector ? {
    instanceId: connector.instanceId,
    endpointId: direction === DIRECTION.target ? connector.target.id : connector.source.id,
    requestType: direction.requestType
} : skipToken
const NO_SYNC_TIME = { lastPull: null, lastPush: null }
const ViewDetails = () => {
    const history = useHistory()
    const location = useLocation()
    const { connectorId } = useParams()
    const params = useMemo(() => new URLSearchParams(location.search), [location.search])
    const direction = directionFor(params.get('direction'))
    const range = useMemo(() => rangeFromParams(params), [params])
    const { connection, isLoading: isConnectionLoading } = useConnection(connectorId)
    const connector = describeConnection(connection)
    const summaries = useEntitySummaries(connection, range)
    const sourceSyncTime = useGetSyncTimeQuery(syncTimeArgs(connector, DIRECTION.source))
    const targetSyncTime = useGetSyncTimeQuery(syncTimeArgs(connector, DIRECTION.target))
    const entities = useMemo(
        () => ({ source: summaries.source.map(toEntityRow), target: summaries.target.map(toEntityRow) }),
        [summaries.source, summaries.target]
    )
    const totals = useMemo(
        () => ({ source: sumEntities(entities.source), target: sumEntities(entities.target) }),
        [entities]
    )
    if (!connector) {
        return isConnectionLoading ? (
            <PageLoader message="Loading sync summary..." />
        ) : (
            <PageNotice
                title="Connector not found"
                hint="It may have been removed, or the link is incomplete."
                linkTo={PATH.MY_PRODUCTS}
                linkLabel="Go to My Products"
            />
        )
    }
    const isSourceToTarget = direction === DIRECTION.source
    const sourceName = connector.source.shortName
    const targetName = connector.target.shortName
    const fromName = isSourceToTarget ? sourceName : targetName
    const toName = isSourceToTarget ? targetName : sourceName
    const syncTime = (isSourceToTarget ? sourceSyncTime.data : targetSyncTime.data) || NO_SYNC_TIME
    const replaceUrl = (nextDirection, nextRange) => {
        history.replace(paths.viewSummary(connectorId, { direction: nextDirection.key, ...rangeToParams(nextRange) }))
    }
    const openDetails = (status, entityId) => {
        history.push(paths.summaryDetails(connectorId, {
            direction: direction.key,
            status,
            entityId: entityId != null ? entityId : undefined,
            ...rangeToParams(range)
        }))
    }
    return (
        <div className="main-content mn-dashboard">
            <div className="page-content custompaddingdash">
                <div className={`container-fluid ${Style.page}`}>
                    <header className={Style.pageHeader}>
                        <div className={Style.pageHeading}>
                            <ol className={`breadcrumb ${Style.breadcrumb}`}>
                                <li className="breadcrumb-item"><Link to={PATH.DASHBOARD}>Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to={paths.productDetail(connectorId)}>{connector.displayName}</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Sync summary</li>
                            </ol>
                            <h1 className={Style.pageTitle}>{sourceName} ⇄ {targetName} sync summary</h1>
                            <p className={Style.pageSubtitle}>
                                Record volumes and outcomes per business entity
                                {range ? <> · <strong>{range.label}</strong></> : ' · All time'}
                            </p>
                        </div>
                        <DateRangeFilter
                            range={range}
                            onChange={(nextRange) => replaceUrl(direction, nextRange)}
                            disabled={summaries.isFetching}
                        />
                    </header>

                    <section className={Style.overview}>
                        <DirectionSwitch
                            sourceName={sourceName}
                            targetName={targetName}
                            isSourceToTarget={isSourceToTarget}
                            onChange={(toTarget) => replaceUrl(toTarget ? DIRECTION.source : DIRECTION.target, range)}
                            totals={totals}
                        />
                        <SyncStatus lastPull={syncTime.lastPull} lastPush={syncTime.lastPush} />
                    </section>

                    <h2 className={Style.sectionTitle}>
                        {fromName} <i className="uil-arrow-right" aria-hidden="true" /> {toName}
                    </h2>

                    <KpiStrip
                        totals={totals[direction.key]}
                        entityCount={entities[direction.key].length}
                        onOpen={openDetails}
                        isLoading={summaries.isFetching}
                    />

                    <EntitySummaryTable
                        key={direction.key}
                        entities={entities[direction.key]}
                        totals={totals[direction.key]}
                        isLoading={summaries.isFetching}
                        loadError={summaries.isError ? 'Unable to load the sync summary. Please try again.' : ''}
                        onRetry={summaries.refetch}
                        onOpen={openDetails}
                        rangeLabel={range ? range.label : null}
                    />
                </div>
            </div>
        </div>
    )
}
export default ViewDetails
